import {
  AUTH_ACTIONS_CLASS,
  AUTH_CONTAINER_CLASS,
  AUTH_FIELDS_CLASS,
  AuthPage,
  AuthPrimaryButton,
  AuthTextField,
} from "@/components/auth";
import { Button, useToast } from "@/components/ui";

import { Form, FormRootError } from "@/components/forms";
import { useStore } from "@/store";
import { handleFormError } from "@/utils/formErrorUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";

const editNumberSchema = z.object({
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\+[1-9]\d{1,14}$/, "Phone number must be in E.164 format (e.g., +1234567890)"),
});

type TEditNumberFormValues = z.infer<typeof editNumberSchema>;

export const EditNumber = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const requestPhoneNumberChange = useStore.use.requestPhoneNumberChange();

  const form = useForm<TEditNumberFormValues>({
    resolver: zodResolver(editNumberSchema),
    defaultValues: { phone: "" },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const onSubmit = form.handleSubmit(async (payload) => {
    form.clearErrors();

    const nextPhoneNumber = payload.phone.trim();
    if (!nextPhoneNumber) {
      form.setError("phone", { type: "manual", message: "Phone number is required" });
      return;
    }

    try {
      const result = await requestPhoneNumberChange(nextPhoneNumber);

      if (!result.success) {
        // handleFormError attaches field-level errors to the input and surfaces
        // everything else as a form-level banner (<FormRootError />). When the
        // store only returns a plain message, mirror that into the banner.
        if (result.errorObject) {
          await handleFormError(result.errorObject, form);
        } else {
          form.setError("root", {
            type: "server",
            message: result.error ?? "We couldn't request a phone number change. Please try again.",
          });
        }
        return;
      }

      toast({
        variant: "success",
        title: "Verification code sent",
        description: result.message ?? "Check WhatsApp for a 6-digit code to verify your phone number.",
      });

      void navigate({
        to: "/auth/verify-otp",
        search: { mode: "phone", phone: nextPhoneNumber, email: undefined, from: undefined },
      });
    } catch (err) {
      console.error("[auth-phone] Failed to request phone number change", { error: err });
      toast({
        variant: "destructive",
        title: "Failed to request phone number change",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  });

  return (
    <Form {...form}>
      <div className={AUTH_CONTAINER_CLASS}>
        <form
          className="grid gap-6"
          onSubmit={(e) => {
            void onSubmit(e);
          }}
        >
          <AuthPage
            title="Edit Number"
            subtitle="Enter your new phone number below to receive a new one-time password (OTP)"
          >
            <div className={AUTH_FIELDS_CLASS}>
              <FormRootError />

              <AuthTextField
                control={form.control}
                name="phone"
                label="Phone"
                inputProps={{ type: "tel", placeholder: "Enter your phone number" }}
              />

              <div className={`${AUTH_ACTIONS_CLASS} flex gap-3`}>
                <AuthPrimaryButton
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Update
                </AuthPrimaryButton>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 px-10"
                  onClick={() => {
                    void navigate({ to: "/app/settings" });
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </AuthPage>
        </form>
      </div>
    </Form>
  );
};
